/**
 * canvas-engine/renderer/elements/elementLink.js
 *
 * OWN tiny, self-contained convert-to-link + background resolver for the Canvas
 * Engine element wrapper.
 *
 * ZERO REUSE: imports NOTHING from `admin-console-ui/src/modules/template-builder/`
 * or `academy-ui/src/template/`. academy-ui cannot import admin-only modules and
 * the Canvas Engine is mirrored verbatim into academy-ui, so href resolution is
 * implemented inline here rather than borrowed from the reader's
 * `templateLinkConfig` helpers. The resolution rules match that contract:
 *   - internal path  → leading-slash path
 *   - external URL   → ensure an `https://` scheme when none is present
 *   - target '_blank' → emit `rel="noopener noreferrer"`
 *
 * Pure functions, no React, no side effects. Used by `CanvasRenderer` to apply a
 * per-element background override and to wrap a link-enabled element's rendered
 * output in a navigable anchor. (Req 4.3, 4.7, 4.8, 5.6)
 */

/**
 * Resolve a per-element background override from an element `style`.
 *
 * Precedence (additive — returns `null` when neither field is set so the
 * caller keeps the element's current rendering):
 *   1. `style.backgroundTransparent === true` → `'transparent'`
 *   2. `style.backgroundColor` is a non-empty string → that color
 *   3. otherwise → `null` (no override)
 *
 * @param {object} [style] normalized element style
 * @returns {string|null} a CSS background value, or `null` for no override
 */
export function elementBackground(style) {
  if (!style || typeof style !== 'object') return null;
  if (style.backgroundTransparent === true) return 'transparent';
  if (typeof style.backgroundColor === 'string' && style.backgroundColor.length > 0) {
    return style.backgroundColor;
  }
  return null;
}

/**
 * Resolve a navigable href from a Link_Config.
 *
 *   - external: keep an existing http(s)/protocol-relative/mailto/tel scheme,
 *     otherwise prefix `https://`.
 *   - internal (and any non-external type): keep an already-absolute URL,
 *     otherwise ensure a single leading slash.
 *
 * @param {object} [link] a normalized Link_Config (`{ type, url, target, enabled }`)
 * @returns {string} the resolved href, or `''` when no usable url exists
 */
export function linkHref(link) {
  if (!link || typeof link !== 'object') return '';
  const url = typeof link.url === 'string' ? link.url.trim() : '';
  if (!url) return '';

  if (link.type === 'external') {
    if (/^https?:\/\//i.test(url)) return url;
    if (/^\/\//.test(url)) return `https:${url}`;
    if (/^(mailto:|tel:)/i.test(url)) return url;
    return `https://${url}`;
  }

  // internal / document / default → leading-slash path (unless already absolute).
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith('/') ? url : `/${url}`;
}

/**
 * Resolve anchor props for a link-enabled element, or `null` when the element is
 * not a link (no `content.link`, `enabled !== true`, or no usable url).
 *
 * @param {object} [link] a normalized Link_Config (+ `enabled` flag)
 * @returns {{href:string, target:(string|undefined), rel:(string|undefined)}|null}
 */
export function elementLinkProps(link) {
  if (!link || typeof link !== 'object' || link.enabled !== true) return null;
  const href = linkHref(link);
  if (!href) return null;

  const target = link.target === '_blank' ? '_blank' : undefined;
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
  return { href, target, rel };
}
